import { useEffect, useRef, useContext } from 'react';
import { select, Selection, EnterElement } from 'd3-selection';

import ChartContext from 'src/components/LineChart/ChartContext';

import { CIRCLE_ICON_RADIUS, OVERLAY_ICONS_SIZE } from 'src/components/RectangleChart/RectangleChart.constants';
import { RectangleChartIconPoint } from 'src/components/RectangleChart/RectangleChart.types';

type RectangleChartIconProps = {
    data: RectangleChartIconPoint[];
    withIcon?: boolean;
    chartIndex?: number;
    verticalAlign?: 'top' | 'center';
    horizontalAlign?: 'between' | 'center';
};

/** Рисует иконку с центром в точке (x, y) */
const drawIcon = (canvas: Selection<EnterElement, unknown, null, undefined>, x: number, y: number, icon: string) => {
    canvas
        .append('foreignObject')
        .attr('x', x - CIRCLE_ICON_RADIUS)
        .attr('y', y - CIRCLE_ICON_RADIUS)
        .attr('width', CIRCLE_ICON_RADIUS * 2)
        .attr('height', CIRCLE_ICON_RADIUS * 2)
        .html(icon);
};

const RectangleChartIcon = ({ data, chartIndex = 0 }: RectangleChartIconProps): JSX.Element => {
    const { xAxis } = useContext(ChartContext).chartData[chartIndex];
    const { width: svgContainerWidth } = useContext(ChartContext);

    const gContainerRef = useRef<SVGGElement | null>(null);

    useEffect(() => {
        if (!gContainerRef.current) {
            return undefined;
        }

        const chart = select(gContainerRef.current).selectAll('circle').data(data).enter();
        chart.each((d, idx, nodes) => {
            const node = select(nodes[idx]);
            if (Array.isArray(d.icon)) {
                drawIcon(node, xAxis(d.startDate) - CIRCLE_ICON_RADIUS + OVERLAY_ICONS_SIZE / 2, 0, d.icon[0]);
                drawIcon(node, xAxis(d.startDate) + CIRCLE_ICON_RADIUS - OVERLAY_ICONS_SIZE / 2, 0, d.icon[1]);
            } else {
                drawIcon(node, xAxis(d.startDate), 0, d.icon);
            }
        });

        return () => {
            chart.selectAll('foreignObject').remove();
        };
    }, [data, xAxis, svgContainerWidth]);

    return <g ref={gContainerRef} />;
};

export default RectangleChartIcon;
